import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Sidebar from '@/components/feature/Sidebar';
import TopBar from '@/components/feature/TopBar';
import Footer from '@/components/feature/Footer';
import Reveal from '@/components/feature/Reveal';
import CategoryFilter from '../home/components/CategoryFilter';
import MenuCard from '../home/components/MenuCard';
import MenuDetailModal from '../home/components/MenuDetailModal';
import { menuItems, type MenuItem } from '@/mocks/menu';

export default function Menu() {
  const { t } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>(() =>
    typeof window !== 'undefined' && window.innerWidth < 768 ? 'list' : 'grid'
  );
  const [selectedItem, setSelectedItem] = useState<MenuItem | null>(null);

  const filteredItems = useMemo(() => {
    let items = menuItems;

    if (activeCategory !== 'all') {
      items = items.filter((item) => item.category === activeCategory);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      items = items.filter(
        (item) =>
          t(`item.${item.id}.name`).toLowerCase().includes(query) ||
          t(`item.${item.id}.desc`).toLowerCase().includes(query) ||
          item.price.toString().includes(query)
      );
    }

    return items;
  }, [activeCategory, searchQuery, t]);

  const groupedByCategory = useMemo(() => {
    const categoryLabels: Record<string, string> = {
      'hot-drinks': t('menuGroup.hotDrinks'),
      'cold-drinks': t('menuGroup.coldDrinks'),
      sweets: t('menuGroup.sweets'),
      food: t('menuGroup.food'),
      breakfast: t('menuGroup.breakfast'),
    };

    if (activeCategory !== 'all') {
      const label = categoryLabels[activeCategory] || t('menu.allItems');
      return { [label]: filteredItems };
    }

    if (searchQuery.trim()) {
      return { [t('menu.allItems')]: filteredItems };
    }

    const groups: Record<string, typeof menuItems> = {};
    const categoryOrder = ['hot-drinks', 'cold-drinks', 'sweets', 'food', 'breakfast'];

    categoryOrder.forEach((cat) => {
      const items = filteredItems.filter((item) => item.category === cat);
      if (items.length > 0) {
        groups[categoryLabels[cat]] = items;
      }
    });

    return groups;
  }, [filteredItems, activeCategory, searchQuery, t]);

  const gridClasses =
    viewMode === 'grid'
      ? 'grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5'
      : 'grid grid-cols-1 gap-4 md:gap-5';

  return (
    <div className="flex min-h-screen bg-background-50">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="flex-1 flex flex-col min-w-0">
        <main className="flex-1 overflow-auto">
          {/* Menu Header */}
          <section className="relative w-full h-0 md:h-[400px] overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Overhead%20view%20of%20an%20elegant%20cafe%20table%20spread%20with%20artisan%20coffee%2C%20fresh%20pastries%20and%20gourmet%20dishes%2C%20warm%20natural%20lighting%2C%20premium%20restaurant%20atmosphere%2C%20appetizing%20food%20photography%2C%20rich%20warm%20tones&width=1600&height=700&seq=menu-hero-01&orientation=landscape"
              alt="BIOLA Cafe Menu"
              className="w-full h-full object-cover object-center hidden md:block"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/40 via-foreground-950/30 to-foreground-950/60 hidden md:block" />
            <div className="absolute inset-0 hidden md:flex items-center justify-center px-4">
              <div className="text-center max-w-2xl w-full">
                <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-background-50 mb-3">
                  {t('menu.title')}
                </h1>
                <p className="text-background-100 text-sm md:text-base max-w-xl mx-auto leading-relaxed">
                  {t('menu.subtitle')}
                </p>
              </div>
            </div>
          </section>

          {/* Menu Body */}
          <section className="w-full px-4 md:px-6 py-6 md:py-10 bg-background-50">
            <div className="max-w-6xl mx-auto">
              <TopBar
                onOpenSidebar={() => setSidebarOpen(true)}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                viewMode={viewMode}
                onViewChange={setViewMode}
              />

              {/* Category Filter */}
              <div className="mb-6 md:mb-8 mt-4 md:mt-6">
                <CategoryFilter
                  activeCategory={activeCategory}
                  onCategoryChange={(cat) => {
                    setActiveCategory(cat);
                    setSearchQuery('');
                  }}
                />
              </div>

              {/* Menu Items */}
              {Object.keys(groupedByCategory).length > 0 ? (
                <div className="space-y-8 md:space-y-10">
                  {Object.entries(groupedByCategory).map(([groupName, items]) => (
                    <Reveal key={groupName}>
                      <div>
                        <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground-950 mb-4 md:mb-5 flex items-center gap-3">
                          <span className="w-1.5 h-5 md:h-6 bg-accent-500 rounded-full flex-shrink-0" />
                          <span className="animate-heading-slide inline-block">
                            {groupName}
                          </span>
                        </h3>
                        <div className={`${gridClasses} items-stretch`}>
                          {items.map((item, index) => (
                            <Reveal key={item.id} delay={index * 55} className="h-full">
                              <MenuCard item={item} viewMode={viewMode} onOpenDetail={setSelectedItem} />
                            </Reveal>
                          ))}
                        </div>
                      </div>
                    </Reveal>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16 md:py-24 animate-fade-in">
                  <span className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-background-100 rounded-full text-secondary-400">
                    <i className="ri-search-line text-3xl" />
                  </span>
                  <p className="text-foreground-600 text-lg font-medium mb-1">
                    {t('menu.noItems')}
                  </p>
                  <p className="text-foreground-400 text-sm">
                    {t('menu.noItemsHint')}
                  </p>
                </div>
              )}
            </div>
          </section>

          <Footer />
        </main>
      </div>

      <MenuDetailModal item={selectedItem} onClose={() => setSelectedItem(null)} />
    </div>
  );
}